import { cn } from "../../lib/utils";
import { IconMenu2, IconX } from "@tabler/icons-react";
import { 
  motion, 
  AnimatePresence,
  useScroll,
  useMotionValueEvent
} from "framer-motion";
import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";

// Helper MotionDiv and MotionButton components
const MotionDiv: React.FC<React.ComponentProps<typeof motion.div>> = (props) => <motion.div {...props} />;
const MotionButton: React.FC<React.ComponentProps<typeof motion.button>> = (props) => <motion.button {...props} />;
const MotionSpan: React.FC<React.ComponentProps<typeof motion.span>> = (props) => <motion.span {...props} />;

interface NavbarProps {
  children: React.ReactNode;
  className?: string;
}

interface NavBodyProps {
  children: React.ReactNode | ((props: { visible: boolean }) => React.ReactNode);
  className?: string;
  visible?: boolean;
}

interface NavItemsProps {
  items: {
    name: string;
    link: string;
    icon?: React.ReactNode;
  }[];
  className?: string;
  onItemClick?: () => void;
}

interface MobileNavProps {
  children: React.ReactNode | ((props: { visible: boolean }) => React.ReactNode);
  className?: string;
  visible?: boolean;
}

interface MobileNavHeaderProps {
  children: React.ReactNode;
  className?: string;
}

interface MobileNavMenuProps {
  children: React.ReactNode;
  className?: string;
  isOpen: boolean;
  onClose: () => void;
}

export const ResizableNavbar = ({ children, className }: NavbarProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const visible = false; // Always keep it in initial state

  return (
    <MotionDiv
      ref={ref}
      className={cn("fixed inset-x-0 top-0 z-50 w-full", className)}
    >
      {React.Children.map(children, (child) =>
        React.isValidElement(child)
          ? React.cloneElement(
              child as React.ReactElement<{ visible?: boolean }>,
              { visible },
            )
          : child,
      )}
    </MotionDiv>
  );
};

export const NavBody = ({ children, className, visible }: NavBodyProps) => {
  return (
    <MotionDiv
      animate={{
        backdropFilter: "blur(12px)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.2)",
        width: "100%",
        y: 0,
        borderRadius: "0px",
      }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 40,
      }}
      className={cn(
        "relative z-[60] mx-auto hidden w-full max-w-none flex-row items-center justify-between self-start px-6 py-4 lg:flex transition-all duration-500",
        // Initial state - full blended navbar with slight rounding
        "bg-[#fafafa]/95 backdrop-blur-xl border-b border-gray-100/50 shadow-lg",
        className,
      )}
    >
      {typeof children === 'function' ? children({ visible: visible || false }) : children}
    </MotionDiv>
  );
};

export const NavItems = ({ items, className, onItemClick }: NavItemsProps) => {
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <MotionDiv
      onMouseLeave={() => setHovered(null)}
      className={cn(
        "absolute inset-0 hidden flex-1 flex-row items-center justify-center space-x-1 text-sm font-medium transition duration-200 lg:flex lg:space-x-1",
        className,
      )}
    >
      {items.map((item, idx) => (
        <a
          onMouseEnter={() => setHovered(idx)}
          onClick={onItemClick}
          className="relative px-4 py-2.5 flex items-center gap-2 rounded-lg transition-all duration-300 text-gray-800 hover:text-primary group"
          key={`link-${idx}`}
          href={item.link}
        >
          {hovered === idx && (
            <MotionDiv
              layoutId="hovered"
              className="absolute inset-0 h-full w-full rounded-lg bg-gray-50/90 shadow-lg backdrop-blur-sm transform transition-transform group-hover:scale-105"
            />
          )}
          <span className="relative z-20 flex items-center gap-2 font-medium whitespace-nowrap overflow-hidden text-ellipsis max-w-[200px]">
            {item.icon}
            {item.name}
          </span>
        </a>
      ))}
    </MotionDiv>
  );
};

export const MobileNav = ({ children, className, visible }: MobileNavProps) => {
  return (
    <MotionDiv
      animate={{
        backdropFilter: "blur(12px)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.2)",
        width: "100%",
        y: 0,
      }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 40,
      }}
      className={cn(
        "fixed top-0 left-0 right-0 z-50 w-full flex flex-col items-center justify-between px-3 py-3 lg:hidden bg-[#fafafa]/95 backdrop-blur-xl border-b border-gray-100/50 shadow-lg transition-all duration-500",
        className,
      )}
    >
      {typeof children === 'function' ? children({ visible: visible || false }) : children}
    </MotionDiv>
  );
};

export const MobileNavHeader = ({
  children,
  className,
}: MobileNavHeaderProps) => {
  return (
    <div
      className={cn(
        "flex w-full flex-row items-center justify-between gap-2 mb-2",
        className,
      )}
    >
      {children}
    </div>
  );
};

export const MobileNavMenu = ({
  children,
  className,
  isOpen,
  onClose,
}: MobileNavMenuProps) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <MotionDiv
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className={cn(
            "absolute inset-x-0 top-20 z-50 flex w-full flex-col items-start justify-start gap-6 rounded-xl bg-white/95 backdrop-blur-xl border border-gray-200/50 px-8 py-10 shadow-xl",
            className,
          )}
        >
          {children}
        </MotionDiv>
      )}
    </AnimatePresence>
  );
};

export const MobileNavToggle = ({
  isOpen,
  onClick,
  visible = false,
}: {
  isOpen: boolean;
  onClick: () => void;
  visible?: boolean;
}) => {
  return (
    <MotionButton
      onClick={onClick}
      className={cn(
        "p-3 rounded-lg border transition-all duration-300 bg-white/80 border-gray-200 text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
        "flex items-center justify-center",
        "shadow-md",
      )}
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.96 }}
      style={{ minWidth: 44, minHeight: 44 }}
    >
      <AnimatePresence mode="wait">
        {isOpen ? (
          <MotionDiv
            key="close"
            initial={{ rotate: -90, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={{ rotate: 90, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <IconX className="h-7 w-7" />
          </MotionDiv>
        ) : (
          <MotionDiv
            key="menu"
            initial={{ rotate: 90, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={{ rotate: -90, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <IconMenu2 className="h-7 w-7" />
          </MotionDiv>
        )}
      </AnimatePresence>
    </MotionButton>
  );
};

interface NavbarLogoProps {
  className?: string;
  logoSrc?: string;
  logoAlt?: string;
  companyName?: string;
  companyTagline?: string;
  href?: string;
  visible?: boolean;
}

export const NavbarLogo = ({
  className,
  logoSrc = "/assets/images/mainwebsiteLogo.png",
  logoAlt = "Company Logo",
  companyName = "Mega Marine",
  companyTagline = "Container Terminals",
  href = "/",
  visible = false,
}: NavbarLogoProps) => {
  return (
    <MotionDiv
      className={cn(
        "relative z-20 flex items-center space-x-2 px-1 py-1 text-sm font-normal rounded-xl group",
        className,
      )}
      whileHover={{ scale: 1.02, y: -2, transition: { duration: 0.3, ease: "easeOut" } }}
      whileTap={{ scale: 0.98, y: 1, transition: { duration: 0.2 } }}
      initial={{ scale: visible ? 0.95 : 1 }}
      animate={{ scale: visible ? 0.95 : 1 }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 30,
      }}
    >
      <Link to={href} className="flex items-center space-x-2">
        <MotionDiv
          className="relative"
          initial={{ width: visible ? "76px" : "90px", height: visible ? "76px" : "90px" }}
          animate={{ width: visible ? "76px" : "90px", height: visible ? "76px" : "90px" }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30,
          }}
        >
          <img
            src={logoSrc}
            alt={logoAlt}
            className="h-full w-full object-contain relative z-10 transition-all duration-300 group-hover:scale-105 rounded-lg shadow-sm"
            onError={(e) => {
              const target = e.currentTarget;
              if (target.src.includes('mainwebsiteLogo.png')) {
                target.src = '/assets/images/Photo1.jpg';
                target.className = 'h-full w-full object-contain relative z-10 transition-all duration-300 hover:scale-105 rounded-lg';
              } else {
                const container = target.parentElement;
                if (container) {
                  target.style.display = 'none';
                  container.innerHTML = `
                    <div class="flex items-center justify-center h-full w-full bg-gradient-to-br from-primary to-ocean rounded-lg">
                      <svg class="h-8 w-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                    </div>
                  `;
                }
              }
            }}
          />
        </MotionDiv>
        <MotionDiv
          className="flex flex-col navbar-logo-text"
          initial={{ opacity: visible ? 0.9 : 1, scale: visible ? 0.95 : 1 }}
          animate={{ opacity: visible ? 0.9 : 1, scale: visible ? 0.95 : 1 }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30,
          }}
        >
          <MotionSpan
            className={cn(
              "font-bold leading-tight transition-all duration-500 tracking-tight text-gray-800 group-hover:text-primary text-sm navbar-logo-text",
            )}
            initial={{ fontSize: visible ? "12px" : "14px" }}
            animate={{ fontSize: visible ? "12px" : "14px" }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
            }}
          >
            {companyName}
          </MotionSpan>
          <MotionSpan
            className={cn(
              "font-semibold leading-tight transition-all duration-500 tracking-tight text-primary/80 group-hover:text-primary whitespace-nowrap text-xs navbar-logo-text",
            )}
            initial={{ fontSize: visible ? "9px" : "11px", opacity: visible ? 0.8 : 1 }}
            animate={{ fontSize: visible ? "9px" : "11px", opacity: visible ? 0.8 : 1 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
            }}
          >
            {companyTagline}
          </MotionSpan>
        </MotionDiv>
      </Link>
    </MotionDiv>
  );
};

export const NavbarButton = ({
  href,
  as: Tag = "a",
  children,
  className,
  variant = "primary",
  ...props
}: {
  href?: string;
  as?: React.ElementType;
  children: React.ReactNode;
  className?: string;
  variant?: "primary" | "secondary" | "dark" | "gradient";
} & (
  | React.ComponentPropsWithoutRef<"a">
  | React.ComponentPropsWithoutRef<"button">
)) => {
  const baseStyles =
    "px-6 py-3 rounded-full font-bold relative cursor-pointer hover:-translate-y-1 hover:shadow-lg transition-all duration-300 inline-block text-center whitespace-nowrap";

  const variantStyles = {
    primary: "bg-primary text-white hover:bg-primary/90 transition-all duration-300 hover:shadow-xl hover:scale-[1.02]",
    secondary: "bg-gray-100 text-gray-900 hover:bg-gray-200 transition-all duration-300 hover:shadow-lg hover:scale-[1.02]",
    dark: "bg-gray-900 text-white hover:bg-gray-800 transition-all duration-300 hover:shadow-xl hover:scale-[1.02]",
    gradient: "bg-gradient-to-r from-primary via-primary-light to-primary text-white hover:from-primary/90 hover:via-primary-light/90 hover:to-primary/90 transition-all duration-300 hover:shadow-xl hover:scale-[1.02]",
  };

  return (
    <MotionDiv
      whileHover={{ scale: 1.05, y: -2 }}
      whileTap={{ scale: 0.95 }}
    >
      <Tag
        href={href || undefined}
        className={cn(baseStyles, variantStyles[variant], className)}
        {...props}
      >
        {children}
      </Tag>
    </MotionDiv>
  );
};

